<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <customer-information
            v-model:editing="editingCustomerInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :show-errors="submitCount > 0"
        />

        <checkout-section-separator />

        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            title="Billing Address"
        />

        <checkout-section-separator v-if="requiresRecurringToken" />

        <recurring-information
            v-if="requiresRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
        />

        <checkout-section-separator v-if="readyForPayment" />

        <payment-information
            v-if="readyForPayment"
            ref="paymentInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :checkout-total="dueToday"
            :title="paymentTitle"
        />

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 font-medium"
        >
            Please fix the errors in red above.
        </div>

        <div>
            <prime-button
                v-if="readyForPayment"
                id="place_order"
                severity="success"
                label="Pay Now"
                type="submit"
                class="w-full"
                :loading="isPending || isValidatingPayment || isProcessing"
            />

            <div v-else>
                <prime-button
                    v-if="dueToday.isZero()"
                    id="place_order"
                    severity="success"
                    label="Complete Purchase"
                    type="submit"
                    class="w-full"
                    :loading="isValidatingRecurring || isProcessing"
                />

                <prime-button
                    v-else
                    label="Continue to Payment"
                    type="submit"
                    class="w-full"
                    :loading="isValidatingRecurring || isProcessing"
                />
            </div>
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'

import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import PrimeButton from 'primevue/button'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type {
    CheckoutRecurringInput,
    CommitCheckoutInput
} from '~/resources/graphql/graphql'
import { computed, ref, toRef, watch } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import { GraphQLRequestError } from 'awesome-graphql-client'
import { useActiveFacility } from '~/composables/use-active-facility'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import CustomerInformation, {
    CustomerInformationCheckoutFormInitialValues,
    CustomerInformationCheckoutFormSchema
} from '~/components/Checkout/Sections/CustomerInformation.vue'
import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import PaymentInformation, {
    PaymentInformationInitialValues,
    PaymentInformationSchema
} from '~/components/Checkout/Sections/PaymentInformation.vue'
import { useShowAlert } from '~/composables/use-show-alert'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import {
    checkoutPaymentInput,
    checkoutRecurringInput
} from '~/components/Checkout/Forms/checkout-helpers'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
}>()

const emit = defineEmits<{
    (
        e: 'checkoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessFragment>
    ): void
}>()

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,

    editingCustomerInformation,
    editingBillingAddress,
    editingRecurringInformation,

    dueToday,
    recurringTotal,
    requiresRecurringToken,

    isProcessing,
    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        customer: CustomerInformationCheckoutFormSchema,
        address: BillingAddressSchema,
        recurring: RecurringInformationSchema,
        payment: PaymentInformationSchema
    })
)

const activeFacility = useActiveFacility()
const { handleSubmit, errors, validateField, validate, setFieldError } =
    useForm({
        validationSchema: schema,
        initialValues: {
            customer: CustomerInformationCheckoutFormInitialValues(
                primaryCustomer.value,
                facility.value,
                activeFacility.value.organization.onlineGuestId
            ),
            address: BillingAddressInitialValues(
                primaryCustomer.value,
                fullAddressRequired.value
            ),
            recurring: RecurringInformationInitialValues(
                requiresRecurringToken.value ? recurringTotal.value : null
            ),
            payment: PaymentInformationInitialValues(dueToday.value)
        }
    })

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(field => !field.startsWith('payment'))
            .length > 0
    )
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(() => pendingRecurringToken.value === false)
const isValidatingRecurring = ref(false)
const isValidatingPayment = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)
const paymentTitle = computed(() => {
    if (requiresRecurringToken.value) {
        return 'Payment Due Today'
    } else {
        return 'Payment'
    }
})

// When editing the recurring information section, reset the form value
watch(editingRecurringInformation, (newValue, oldValue) => {
    if (oldValue === false && newValue === true) {
        pendingRecurringToken.value = true
    }
})

// References to components allowing us to submit them
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()
const paymentInformationRef = ref<InstanceType<typeof PaymentInformation>>()

// Main submit function
async function handleSubmitCheckoutForm(event: SubmitEvent) {
    // Prevent form action
    event.preventDefault()
    submitCount.value += 1

    if (readyForPayment.value) {
        isValidatingPayment.value = true
        try {
            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return key.startsWith('payment') || value.valid
                }
            )

            if (isValid) {
                if (!result.results.payment?.valid) {
                    // Also clear this error so we don't flash red on the payment error
                    // onsubmit will validate the whole form again
                    setFieldError('payment', undefined)
                    await paymentInformationRef.value.submitPaymentForm()
                }

                return onSubmit(event)
            } else {
                // TODO: Find a way to scroll up
            }
        } finally {
            isValidatingPayment.value = false
        }

        return
    }

    // Recurring checkout
    try {
        isValidatingRecurring.value = true

        // If recurring field is not valid, submit stripe form
        // We use silent so we don't show the error message until after stripe form is submitted
        const { valid: validRecurring } = await validateField('recurring', {
            mode: 'silent'
        })
        if (!validRecurring) {
            await recurringInformationRef.value.submitRecurringForm()
        }

        // Otherwise, validate the entire form
        const result = await validate()
        const isValid = Object.entries(result.results).every(([key, value]) => {
            return key.startsWith('payment') || value.valid
        })

        // Also clear this error so we don't flash red on the payment error
        setFieldError('payment', undefined)

        // Ready for payment!
        if (isValid) {
            // Zero-dollar transaction, so submit the form
            if (dueToday.value.isZero()) {
                return onSubmit(event)
            }
            // Close all the other sections
            else {
                submitCount.value = 0 // reset submit count
                editingCustomerInformation.value = false
                editingBillingAddress.value = false
                pendingRecurringToken.value = false
                editingRecurringInformation.value = false
            }
        } else {
            // TODO: Find a way to scroll up
        }
    } finally {
        isValidatingRecurring.value = false
    }
}

const query = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitCheckoutInput) => {
        const response = await query(
            graphql(/** GraphQL */ `
                mutation CommitCheckoutCustomer($input: CommitCheckoutInput!) {
                    commitCheckout(input: $input) {
                        __typename
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessFragment
                        }
                        ... on CommitCheckoutIncomplete {
                            count
                            results
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData
            }
        )

        if (response.commitCheckout === null) {
            throw new Error(
                'Oops! Something went wrong with the checkout. Don’t worry, it happens sometimes. Please try again, and if the issue continues, contact your facility for help! (Code: CFOC)'
            )
        }

        if (
            response.commitCheckout.__typename === 'CommitCheckoutResult' ||
            response.commitCheckout.__typename === 'CommitCheckoutIncomplete' ||
            response.commitCheckout.__typename === 'ValidationError'
        ) {
            return response.commitCheckout
        }

        throw new Error()
    }
})

// Once form is validated, this preps and calls the mutation
const showAlert = useShowAlert()
const queryClient = useQueryClient()
const onSubmit = handleSubmit(values => {
    // Customer and Address
    const customer = {
        ...values.customer,
        dateOfBirth: null,
        mobilePhone: values.customer.mobilePhone.number,
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Add date of birth if it exists
    if (values.customer.dateOfBirth !== '') {
        customer.dateOfBirth = values.customer.dateOfBirth
    }

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    // Payment Items
    const payment = checkoutPaymentInput(values.payment)

    incrementProcessing()

    mutate(
        {
            checkoutId: checkoutId.value,
            customer,
            recurring,
            payment
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    // Warn, even for invalid initials here
                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        messageBuilder += error.message
                    })
                    if (messageBuilder !== '') {
                        showAlert('Heads Up', messageBuilder)
                    }
                } else if (data.__typename === 'CommitCheckoutIncomplete') {
                    // Invalidate the checkout query to refresh
                    queryClient.invalidateQueries({
                        predicate: query =>
                            query.queryKey.includes('CheckoutViewerQuery')
                    })

                    // Pop-up an alert for the user
                    showAlert(
                        'Heads Up',
                        'You have ' +
                            data.count +
                            ' incomplete contract(s)! Please edit or remove that contract(s) and try again.'
                    )

                    if (import.meta.env.DEV) {
                        console.log(data.results)
                    }
                } else {
                    emit('checkoutSuccess', data)
                }
            },
            onError(error) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert('Heads Up', String(error))
                }
            },
            onSettled() {
                decrementProcessing()
            }
        }
    )
})
</script>
